/**
 * Zentrale Fahrzeugtyp-Konfiguration
 *
 * Alle Fahrzeugtypen, Sitzplätze, Spezialfähigkeiten und Kompatibilitätslogik
 * werden hier definiert und von allen Komponenten importiert.
 */

// Fahrzeugtypen mit Bezeichnung und Sitzplätzen
export const VEHICLE_TYPES = {
  1: { label: 'PKW', seats: 4, description: 'PKW (bis 4 Sitzplätze)' },
  2: { label: 'Großraum', seats: 6, description: 'Großraum (bis 6 Sitzplätze)' },
  3: { label: 'Kleinbus', seats: 7, description: 'Kleinbus (bis 7 Sitzplätze)' },
  4: { label: 'Kleinbus', seats: 8, description: 'Kleinbus (bis 8 Sitzplätze)' },
};

// Array für Select-Dropdowns
export const VEHICLE_TYPE_OPTIONS = [
  { value: 1, label: 'PKW (bis 4 Sitzplätze)' },
  { value: 2, label: 'Großraum (bis 6 Sitzplätze)' },
  { value: 3, label: 'Kleinbus (bis 7 Sitzplätze)' },
  { value: 4, label: 'Kleinbus (bis 8 Sitzplätze)' },
];

// Alle verfügbaren Spezialfähigkeiten
export const SPECIAL_CAPABILITIES = {
  kombi: 'Kombi',
  limousine: 'Limousine',
  rollstuhl: 'Rollstuhlfahrzeug',
  tragestuhl: 'Tragestuhlfahrzeug',
  liegend: 'Liegendfahrzeug',
  tiefer_einstieg: 'Tiefer Einstieg',
  hoher_einstieg: 'Hoher Einstieg',
};

// Spezialfähigkeiten, die sich in einer Bestellung gegenseitig ausschließen
// (nextcab_mobile: docs/general/spezialfaehigkeiten_ausschluss_konzept.md).
// Ein Fahrzeug darf mehrere davon haben — es *kann* Rollstuhl und Tragestuhl —,
// ein Fahrgast braucht nur eine. Synchron halten mit
// `exclusiveCapabilityGroups` in lib/utils/ride_capabilities.dart der App.
export const EXCLUSIVE_CAPABILITY_GROUPS = [
  ['rollstuhl', 'tragestuhl', 'liegend'],
  ['tiefer_einstieg', 'hoher_einstieg'],
  ['kombi', 'limousine'],
];

// Fähigkeiten aus `selected`, die `capability` widersprechen.
export const conflictingCapabilities = (capability, selected) => {
  const group = EXCLUSIVE_CAPABILITY_GROUPS.find((g) => g.includes(capability));
  if (!group) return [];
  return selected.filter((other) => other !== capability && group.includes(other));
};

// Neue Auswahl einer Mehrfachauswahl: Neu hinzugekommene Fähigkeiten verdrängen
// bereits gewählte derselben Gruppe. Altkombinationen, die niemand anfasst,
// bleiben unverändert. Kommen mehrere auf einmal dazu, gewinnt die zuletzt gewählte.
export const applyCapabilityExclusions = (previous, next) => {
  const added = next.filter((cap) => !previous.includes(cap));
  let result = next;
  [...added].reverse().forEach((cap) => {
    if (!result.includes(cap)) return;
    const conflicts = conflictingCapabilities(cap, result);
    result = result.filter((other) => !conflicts.includes(other));
  });
  return result;
};

// Hier stand einmal CAPABILITIES_PER_TYPE — eine Tabelle, welche Fähigkeit zu
// welchem Fahrzeugtyp "passt". Die Annahme trägt nicht: Ein fünfsitziger Caddy
// wird je nach Betrieb als PKW oder als Großraum angelegt und kann trotzdem ein
// Rollstuhlfahrzeug sein. Das Fahrzeugformular hat die Kopplung deshalb schon
// aufgegeben (0e18bd2), die Bestellmaske war der letzte Nutzer und leitet ihre
// Auswahl jetzt aus dem tatsächlichen Fahrzeugbestand ab. Bitte nicht wieder
// einführen: Fähigkeiten sind eine Eigenschaft des Fahrzeugs, nicht des Typs.

// Kompatibilitätsmatrix: Welche Fahrzeugtypen können eine Bestellung bedienen?
// Aufsteigend: Größere Fahrzeuge können kleinere Bestellungen annehmen
export const getCompatibleVehicleTypes = (orderVehicleType) => {
  switch (orderVehicleType) {
    case 1: return [1, 2, 3, 4];
    case 2: return [2, 3, 4];
    case 3: return [3, 4];
    case 4: return [4];
    default: return [1, 2, 3, 4];
  }
};

// Prüft ob ein Fahrzeugtyp eine Bestellung bedienen kann
export const isVehicleCompatible = (driverVehicleType, orderVehicleType) => {
  return getCompatibleVehicleTypes(orderVehicleType).includes(driverVehicleType);
};

// Label für einen Fahrzeugtyp
export const getVehicleTypeLabel = (type) => {
  return VEHICLE_TYPES[type]?.label || 'Unbekannt';
};

// Sitzplätze für einen Fahrzeugtyp
export const getVehicleTypeSeats = (type) => {
  return VEHICLE_TYPES[type]?.seats || 0;
};

// Vollständige Beschreibung für einen Fahrzeugtyp (z.B. für Dropdowns)
export const getVehicleTypeDescription = (type) => {
  return VEHICLE_TYPES[type]?.description || 'Unbekannt';
};

// Label für eine Spezialfähigkeit
export const getCapabilityLabel = (capability) => {
  return SPECIAL_CAPABILITIES[capability] || capability;
};

// Formatiert Fahrzeugtyp + Capabilities als Display-String
export const formatVehicleWithCapabilities = (vehicleType, capabilities) => {
  const typeNum = Number(vehicleType);
  let typeLabel = getVehicleTypeLabel(vehicleType);
  // Kleinbus-Varianten unterscheiden (7 vs. 8 Sitzplätze)
  if (typeNum === 3 || typeNum === 4) {
    typeLabel = `${typeLabel} ${getVehicleTypeSeats(vehicleType)} Sitzplätze`;
  }
  if (!capabilities || capabilities.length === 0) return typeLabel;
  const capLabels = capabilities.map(c => getCapabilityLabel(c)).join(', ');
  return `${typeLabel} (${capLabels})`;
};

// Backward-Compat: Alten specialVehicleType-String in Array umwandeln
export const migrateSpecialVehicleType = (data) => {
  if (data.specialCapabilities && Array.isArray(data.specialCapabilities)) {
    return data.specialCapabilities;
  }
  if (data.specialVehicleType) {
    // kleinbus_7 und kleinbus_8 sind jetzt eigene Typen, keine Capabilities mehr
    if (['kleinbus_7', 'kleinbus_8'].includes(data.specialVehicleType)) {
      return [];
    }
    return [data.specialVehicleType];
  }
  return [];
};
